import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from 'components/Button/Button';
import { paths } from 'routes';
import RadioInput from 'components/Inputs/RadioInput/RadioInput';
import CareReferralForm from 'components/Forms/ReferralForms/CareReferralForm';
import SpecialtyReferralForm from 'components/Forms/ReferralForms/SpecialtyReferralForm';
import TherapyReferralForm from 'components/Forms/ReferralForms/TherapyReferralForm';
import MedicalReferralForm from 'components/Forms/ReferralForms/MedicalReferralForm';
import Modal from 'components/Modal/Modal';
import TextBoxInput from 'components/Inputs/TextBoxInput/TextBoxInput';
import useValidator from 'hooks/useValidator';
import { useMutation } from '@tanstack/react-query';
import { updateQuestionnaire } from 'api/api';
import { useAppContext } from '../../context/appContext';
import Icon from '../../components/Icon/Icon';

function PatientReferral() {
  const [{ patients }] = useAppContext();
  const [referralType, setReferralType] = React.useState('');
  const { visitId } = useParams<{ visitId: string }>();

  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const rType = params.get('rType');

  const patientId = params.get('patient');

  const referralTypes = {
    'Primary Care Referral': {
      heading: 'In-person Primary Care',
      subtitle: 'Provide a few details about this request',
    },
    'Specialty Referral': {
      heading: 'Specialty Care',
      subtitle: 'Provide a few details about this request',
    },
    'Therapy Referral': {
      heading: 'Therapy Referral',
      subtitle: 'Provide a few details about this request',
    },
    'Request Medical History': {
      heading: 'Medical History Request',
      subtitle: 'Provide a few details about this request',
    },
    Other: {
      heading: 'Other',
      subtitle: 'Provide a few details about this request',
    },
  };

  const patient = patients?.find(fndPatient => fndPatient.id === patientId);

  const { mutate: sendReferralFn, status } = useMutation(
    (variables: { questionnaireId: string; payload: Record<string, any> }) =>
      updateQuestionnaire(variables.questionnaireId, variables.payload),
    {
      onSettled: () => {},
    },
  );

  const goToTreatmentTab = (statusT: string) => {
    if (!visitId) return;

    if (statusT === 'error') {
      return;
    }

    params.delete('rType');
    const treatmentPlanURL = `${paths.TREATMENT_PLAN_URL_PATH.replace(
      ':visitId',
      visitId,
    )}?${params.toString()}`;

    window.location.replace(treatmentPlanURL);
    // navigate(treatmentPlanURL);
  };

  const handleSelectedReferralType = () => {
    if (!visitId) return;
    params.set('rType', referralType);
    const referralURL = `${paths.PATIENT_REFERRAL_TICKET_PATH.replace(
      ':visitId',
      visitId,
    )}?${params.toString()}`;

    navigate(referralURL);
  };

  const handleSendReferral = (referral: any) => {
    if (!visitId) return;
    sendReferralFn({
      questionnaireId: visitId,
      payload: {
        id: visitId,
        referrals: [{ ...referral }],
      },
    });
  };

  return (
    <>
      <div className="relative pt-8 tablet:pt-14 pb-14 flex flex-col justify-center items-center">
        <button
          onClick={() => navigate(-1)}
          type="button"
          className="absolute top-14 left-0 hidden tablet:flex flex-row items-center"
        >
          <Icon id="goBack" className="w-8 h-8" />
        </button>
        <div className="w-full tablet:w-[90%] laptop:w-[50%] text-center">
          <h2 className="font-semibold text-[20px] text-river-black">
            {rType ? (referralTypes as any)[rType]?.heading : 'Patient Referral'}
          </h2>
          <p className="text-sm mt-1 text-river-black">
            {rType
              ? (referralTypes as any)[rType]?.subtitle
              : 'Refer patients to specialists and other healthcare providers.'}
          </p>
        </div>
        <div className="w-full tablet:w-[90%] laptop:w-[40%] mt-[40px]">
          {!rType ? (
            <>
              <h3 className="text-lg font-normal leading-8 mb-4">
                What type of request do you want to submit?
              </h3>
              <div className="flex flex-col space-y-4 w-full">
                <label
                  htmlFor="primaryCare"
                  className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
                >
                  <RadioInput
                    id="primaryCare"
                    name="referralType"
                    value="Primary Care Referral"
                    checked={referralType === 'Primary Care Referral'}
                    onChange={() => setReferralType('Primary Care Referral')}
                    className="align-middle cursor-pointer"
                  />{' '}
                  <span className="align-middle text-sm ml-2 font-normal">
                    In-Person Primary Care Referral
                  </span>
                </label>
                <label
                  htmlFor="specialty"
                  className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
                >
                  <RadioInput
                    id="specialty"
                    name="referralType"
                    value="Specialty Referral"
                    checked={referralType === 'Specialty Referral'}
                    onChange={() => setReferralType('Specialty Referral')}
                    className="align-middle cursor-pointer"
                  />{' '}
                  <span className="align-middle text-sm ml-2 font-normal">Specialty Referral</span>
                </label>
                <label
                  htmlFor="therapy"
                  className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
                >
                  <RadioInput
                    id="therapy"
                    name="referralType"
                    value="Therapy Referral"
                    checked={referralType === 'Therapy Referral'}
                    onChange={() => setReferralType('Therapy Referral')}
                    className="align-middle cursor-pointer"
                  />{' '}
                  <span className="align-middle text-sm ml-2 font-normal">Therapy Referral</span>
                </label>
                <label
                  htmlFor="medicalHistory"
                  className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
                >
                  <RadioInput
                    id="medicalHistory"
                    name="referralType"
                    value="Request Medical History"
                    checked={referralType === 'Request Medical History'}
                    onChange={() => setReferralType('Request Medical History')}
                    className="align-middle cursor-pointer"
                  />{' '}
                  <span className="align-middle text-sm ml-2 font-normal">
                    Request Medical History
                  </span>
                </label>
                <label
                  htmlFor="other"
                  className="cursor-pointer checkbox-form-input transition-all linear h-fit !py-[0.47rem"
                >
                  <RadioInput
                    id="other"
                    name="referralType"
                    value="Other"
                    checked={referralType === 'Other'}
                    onChange={() => setReferralType('Other')}
                    className="align-middle cursor-pointer"
                  />{' '}
                  <span className="align-middle text-sm ml-2 font-normal">Other</span>
                </label>
              </div>

              <div className="mt-20 text-center">
                <Button
                  label="Continue"
                  onClick={() => handleSelectedReferralType()}
                  className="!px-2 !min-w-[250px]"
                />
              </div>
            </>
          ) : rType === 'Primary Care Referral' && patient ? (
            <CareReferralForm
              type={rType}
              patient={patient}
              onSubmitReferral={payload => {
                handleSendReferral(payload);
              }}
            />
          ) : rType === 'Specialty Referral' ? (
            <SpecialtyReferralForm
              type={rType}
              onSubmitReferral={payload => {
                handleSendReferral(payload);
              }}
            />
          ) : rType === 'Therapy Referral' && patient ? (
            <TherapyReferralForm
              patient={patient}
              type={rType}
              onSubmitReferral={payload => {
                handleSendReferral(payload);
              }}
            />
          ) : rType === 'Request Medical History' ? (
            <MedicalReferralForm
              type={rType}
              onSubmitReferral={payload => {
                handleSendReferral(payload);
              }}
            />
          ) : rType === 'Other' ? (
            <OtherForm
              type={rType}
              onSubmitReferral={payload => {
                handleSendReferral(payload);
              }}
            />
          ) : null}
        </div>
      </div>

      <Modal show={status && status !== 'idle'}>
        <div className="rounded-xl bg-white tablet:w-[500px]">
          <div className="flex flex-col justify-center items-center px-8 pb-10 pt-6 text-center">
            {status !== 'success' ? (
              <img
                src={
                  status === 'loading'
                    ? '/assets/images/loader_min.gif'
                    : '/assets/images/error_min.gif'
                }
                alt="screen status"
                className="-mt-[15px]"
                width={170}
                height={170}
              />
            ) : (
              <span
                className="bg-[#F5FAF4] w-[70px] h-[70px] rounded-full flex flex-row justify-center 
                items-center mb-4 mt-4"
              >
                <Icon id="checkmark" className="text-[#72C16D] w-[20px]" />
              </span>
            )}

            <p className="text-lg font-medium tracking-wider">
              {status === 'loading'
                ? 'Submitting referral... Almost there!'
                : status === 'error'
                ? 'Oops! We encountered an issue submitting this referral. Please retry.'
                : 'All done. Patient referral submitted successfully.'}
            </p>

            {['error', 'success'].includes(status) && (
              <button
                type="button"
                onClick={() => goToTreatmentTab(status)}
                className="w-fit bg-river-blue border border-river-blue text-white rounded-full px-5 py-[12px] text-sm mt-8 min-w-[200px] 
                    hover:opacity-90 disabled:hover:opacity-100 disabled:text-gray-300/60 disabled:bg-river-blue/80 
                    disabled:cursor-cursor-not-allowed disabled:border-transparent"
              >
                {status === 'error' ? 'Try again' : 'All set!'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

function OtherForm({
  type,
  onSubmitReferral,
}: {
  type: string;
  onSubmitReferral: (payload: Record<string, string>) => void;
}) {
  const [validator, showValidationMessage] = useValidator({});
  const [referralNotes, setReferralNotes] = React.useState('');

  const handleSubmit = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    onSubmitReferral({
      type,
      notes: referralNotes,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm leading-8 mb-2">Add a referral note or comment</h3>
        <TextBoxInput
          name="referralNotes"
          placeholder="Type your response here..."
          value={referralNotes}
          onChange={event => setReferralNotes(event.target.value)}
          rows={5}
          className="peer-[.validator-error]/referralNotes:!border-river-red peer-[.validator-error]/referralNotes:!mb-5 
          peer-[.validator-error]/referralNotes:focus:!ring-river-red"
          validator={validator.message('referralNotes', referralNotes, 'required|string|min:2', {
            messages: {
              required: 'Please add a referral notes or comments.',
            },
            className: 'peer/referralNotes validator-error mt-3 top-[150px] ml-1',
          })}
        />
      </div>

      <div className="!mt-20 text-center">
        <Button label="Submit" onClick={() => handleSubmit()} className="!px-2 !min-w-[250px]" />
      </div>
    </div>
  );
}

export default PatientReferral;
